import { useConfig } from '../lib/ConfigContext';
import { Link, useLocation } from 'react-router-dom';
import { LogOut, Plus, Home, Server, Inbox } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import type { ReactNode } from 'react';

export function Layout({ children }: { children: ReactNode }) {
  const config = useConfig();
  const { signOut, email } = useAuth();
  const location = useLocation();

  const navLinkClass = (active: boolean) =>
    `inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition ${
      active
        ? 'bg-slate-100 text-brand-ink'
        : 'text-slate-600 hover:text-brand-ink hover:bg-slate-100'
    }`;

  const onHomesRoute =
    location.pathname === '/' || location.pathname.startsWith('/homes');
  const onEdgesRoute = location.pathname.startsWith('/edges');
  const onRegistrationsRoute = location.pathname.startsWith('/registrations');

  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur">
        <div className="max-w-6xl mx-auto px-6 py-4 flex flex-wrap gap-4 items-center justify-between">
          <div className="flex flex-wrap items-center gap-4">
            <Link to="/" className="flex items-baseline gap-2.5 group">
              <span className="wordmark text-xl">MANOR</span>
              <span className="text-[10px] text-slate-500 uppercase tracking-[0.24em] font-medium">
                Admin {config.conciergeOnly && '· TEST'}
              </span>
            </Link>
            <nav className="flex flex-wrap items-center gap-1">
              <Link
                to="/concierge"
                className={navLinkClass(
                  location.pathname.startsWith('/concierge'),
                )}
              >
                <Inbox size={14} /> Concierge
              </Link>
              {!config.conciergeOnly && (
                <>
                  <Link to="/" className={navLinkClass(onHomesRoute)}>
                    <Home size={14} /> Homes
                  </Link>
                  <Link to="/edges" className={navLinkClass(onEdgesRoute)}>
                    <Server size={14} /> Edges
                  </Link>
                  <Link
                    to="/registrations"
                    className={navLinkClass(onRegistrationsRoute)}
                  >
                    <Inbox size={14} /> Registrations
                  </Link>
                </>
              )}
            </nav>
          </div>
          <div className="flex items-center gap-3 text-sm">
            {!config.conciergeOnly && (
              <Link
                to="/homes/new"
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-brand-ink hover:bg-ink-800 text-brand-bone font-medium transition shadow-sm"
              >
                <Plus size={15} /> New Home
              </Link>
            )}
            <span className="text-slate-500 hidden sm:inline text-xs">
              {email}
            </span>
            <button
              onClick={signOut}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-300 hover:border-slate-400 text-slate-600 hover:text-brand-ink transition"
              aria-label="Sign out"
              title="Sign out"
            >
              <LogOut size={14} />
            </button>
          </div>
        </div>
      </header>
      <main className="flex-1 max-w-6xl w-full mx-auto px-6 py-8">
        {children}
      </main>
      <footer className="border-t border-slate-200 text-xs text-slate-500 py-4 text-center">
        {config.conciergeOnly ? 'Concierge TEST' : 'admin.mymanor.click'} · {location.pathname}
      </footer>
    </div>
  );
}
